import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import PageSEO from '../components/PageSEO';
import {
  Shield, Brain, Bone, Droplet, Layers, Flame, Wheat, Heart,
  Users, Clock, MapPin, TrendingUp, ArrowRight, AlertTriangle,
  Sparkles, ChevronDown, Info, Activity, ExternalLink
} from 'lucide-react';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell,
} from 'recharts';
import { InsightBox } from '../components/InsightBox';
import { CsvDownloadButton } from '../components/CsvDownloadButton';
import { ComplexConditionsSection } from '../components/ComplexConditionsSection';
import { ShareButtons } from '../components/ShareButtons';
import { SITE_MEDIA } from '../lib/siteMedia';
import { STATE_FIPS, GEO_URL, getRiskColor } from '../lib/mapConstants';
import { fetchWithFallback } from '../lib/dataFetcher';
const API = process.env.REACT_APP_BACKEND_URL;

// Clean hero image without text
const AUTOIMMUNE_HERO_IMAGE = 'https://images.pexels.com/photos/3825539/pexels-photo-3825539.jpeg?auto=compress&w=1200';

const DISEASE_META = {
  rheumatoid_arthritis: { icon: Bone, color: '#f97316', label: 'Rheumatoid Arthritis' },
  lupus: { icon: Sparkles, color: '#8b5cf6', label: 'Lupus (SLE)' },
  multiple_sclerosis: { icon: Brain, color: '#ef4444', label: 'Multiple Sclerosis' },
  type1_diabetes: { icon: Droplet, color: '#0ea5e9', label: 'Type 1 Diabetes' },
  psoriasis: { icon: Layers, color: '#10b981', label: 'Psoriasis' },
  celiac_disease: { icon: Wheat, color: '#f59e0b', label: 'Celiac Disease' },
  crohns_disease: { icon: Flame, color: '#dc2626', label: "Crohn's Disease" },
  hashimotos: { icon: Shield, color: '#6366f1', label: "Hashimoto's" },
  graves_disease: { icon: Flame, color: '#ea580c', label: "Graves' Disease" },
  sjogrens_syndrome: { icon: Droplet, color: '#0d9488', label: "Sjögren's Syndrome" },
  ulcerative_colitis: { icon: Flame, color: '#b91c1c', label: 'Ulcerative Colitis' },
  myasthenia_gravis: { icon: Activity, color: '#7c2d12', label: 'Myasthenia Gravis' },
  vitiligo: { icon: Layers, color: '#fbbf24', label: 'Vitiligo' },
  scleroderma: { icon: Shield, color: '#581c87', label: 'Scleroderma' },
  addisons_disease: { icon: Activity, color: '#064e3b', label: "Addison's Disease" },
  ankylosing_spondylitis: { icon: Bone, color: '#854d0e', label: 'Ankylosing Spondylitis' },
  alopecia_areata: { icon: Layers, color: '#a16207', label: 'Alopecia Areata' },
  pernicious_anemia: { icon: Droplet, color: '#991b1b', label: 'Pernicious Anemia' },
  polymyalgia_rheumatica: { icon: Activity, color: '#b45309', label: 'Polymyalgia Rheumatica' },
  primary_biliary_cholangitis: { icon: Activity, color: '#065f46', label: 'Primary Biliary Cholangitis' },
  dermatomyositis: { icon: Layers, color: '#7c2d12', label: 'Dermatomyositis' },
  guillain_barre: { icon: Activity, color: '#2563eb', label: 'Guillain-Barré Syndrome' },
  autoimmune_hepatitis: { icon: Activity, color: '#dc2626', label: 'Autoimmune Hepatitis' },
  antiphospholipid_syndrome: { icon: Droplet, color: '#7c3aed', label: 'Antiphospholipid Syndrome' },
  behcets_disease: { icon: Flame, color: '#e11d48', label: "Behçet's Disease" },
};

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white shadow-lg border border-slate-200 rounded-xl px-4 py-3 text-sm">
      <p className="font-semibold text-slate-700 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }} className="font-medium">{p.name}: {p.value}%</p>
      ))}
    </div>
  );
};

const anim = (d = 0) => ({ initial: { opacity: 0, y: 15 }, animate: { opacity: 1, y: 0 }, transition: { delay: d, duration: 0.4 } });

export default function AutoimmuneDiseases() {
  const [diseases, setDiseases] = useState({});
  const [mapData, setMapData] = useState([]);
  const [selectedDisease, setSelectedDisease] = useState('rheumatoid_arthritis');
  const [hoveredState, setHoveredState] = useState(null);
  const [expandedDisease, setExpandedDisease] = useState(null);
  const [activeLayers, setActiveLayers] = useState(['rheumatoid_arthritis']);

  useEffect(() => {
    fetchWithFallback('/api/autoimmune/diseases').then(d => d && setDiseases(d.diseases || {})).catch(() => {});
  }, []);

  useEffect(() => {
    fetchWithFallback(`/api/autoimmune/map-data?disease=${selectedDisease}`).then(d => d && setMapData(d.data || [])).catch(() => {});
  }, [selectedDisease]);

  const mapLookup = useMemo(() => {
    const m = {};
    mapData.forEach(d => { m[d.state] = d; });
    return m;
  }, [mapData]);

  const toggleLayer = (key) => {
    setSelectedDisease(key);
    setActiveLayers(prev =>
      prev.includes(key) ? (prev.length > 1 ? prev.filter(l => l !== key) : prev) : [...prev, key]
    );
  };

  const currentDiseaseData = diseases[selectedDisease] || {};
  const meta = DISEASE_META[selectedDisease] || { icon: Shield, color: '#6366f1', label: selectedDisease };

  // Prevalence comparison chart data
  const prevalenceData = Object.entries(diseases).map(([key, d]) => ({
    name: d.name?.split(' ')[0] || key,
    prevalence: d.us_prevalence || 0,
    fill: DISEASE_META[key]?.color || '#6366f1'
  })).sort((a, b) => b.prevalence - a.prevalence);

  return (
    <div className="min-h-screen bg-slate-50/50" data-testid="autoimmune-page">
      <PageSEO title="Autoimmune Disease Dashboard" description="Explore 25 autoimmune diseases with US prevalence data, state-level maps, symptoms, triggers, and support resources. Data sourced from NIH, AARDA, and CDC." path="/autoimmune" />
      {/* Hero Banner - Clean image without text */}
      <section className="relative bg-gradient-to-br from-purple-900 via-violet-900 to-indigo-900 overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img src={AUTOIMMUNE_HERO_IMAGE} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-purple-900/90 via-violet-900/70 to-transparent" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <motion.div {...anim(0)} className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-purple-200 text-sm font-medium mb-5" data-testid="autoimmune-badge">
              <Shield className="w-4 h-4" /> Autoimmune Disease Intelligence
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black text-white tracking-tight leading-tight mb-4 drop-shadow-lg">
              Understanding<br />
              <span className="bg-gradient-to-r from-purple-300 to-pink-300 bg-clip-text text-transparent">Autoimmune Diseases</span>
            </h1>
            <p className="text-base sm:text-lg text-white/90 leading-relaxed max-w-2xl drop-shadow-md">
              Over 80 autoimmune diseases affect an estimated 24 million Americans. Explore patterns, diagnostic delays, symptoms, and prevalence data.
            </p>
          </motion.div>

          {/* Key Stats */}
          <motion.div {...anim(0.15)} className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-8">
            {[
              { label: 'Americans Affected', value: '24M', icon: Users, color: 'bg-purple-500/40' },
              { label: 'Known Diseases', value: '80+', icon: Brain, color: 'bg-violet-500/40' },
              { label: 'Patients Female', value: '78%', icon: Heart, color: 'bg-pink-500/40' },
              { label: 'Avg Diagnosis Delay', value: '4.5yr', icon: Clock, color: 'bg-indigo-500/40' },
            ].map((s, i) => (
              <div key={i} className={`${s.color} backdrop-blur-sm rounded-2xl border border-white/20 p-4`} data-testid={`ai-stat-${i}`}>
                <s.icon className="w-5 h-5 text-white/80 mb-2" />
                <p className="text-2xl font-bold text-white">{s.value}</p>
                <p className="text-xs text-white/70">{s.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">

        {/* ========== DISEASE SELECTOR + NATIONAL PREVALENCE EXPLAINER ========== */}
        <motion.section {...anim(0.1)} data-testid="autoimmune-map-section">
          <div className="flex flex-wrap items-end justify-between mb-5 gap-4">
            <div>
              <h2 className="text-xl font-bold text-slate-900 mb-1 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-purple-600" /> Pick a disease to explore
              </h2>
              <p className="text-sm text-slate-500">Each disease links to its primary national authority for the prevalence figure.</p>
            </div>
            <div className="flex items-center gap-2">
              <ShareButtons title={`Autoimmune Disease - ${meta.label}`} text={`Explore ${meta.label} on EpiSplain`} />
            </div>
          </div>

          {/* Disease toggle buttons */}
          <div className="flex flex-wrap gap-2 mb-5" data-testid="map-layer-toggles">
            {Object.entries(DISEASE_META).map(([key, m]) => {
              const isActive = selectedDisease === key;
              return (
                <button
                  key={key}
                  onClick={() => toggleLayer(key)}
                  data-testid={`layer-toggle-${key}`}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-semibold border transition-all ${
                    isActive
                      ? 'text-white border-transparent shadow-sm'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                  }`}
                  style={isActive ? { backgroundColor: m.color } : {}}
                >
                  <m.icon className="w-3.5 h-3.5" />
                  {m.label}
                </button>
              );
            })}
          </div>

          {/* National prevalence card + Why no state map explainer */}
          <div className="grid lg:grid-cols-3 gap-6">
            {/* National prevalence card */}
            <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200/60 p-6 shadow-sm" data-testid="autoimmune-national-prevalence">
              <div className="flex items-start gap-4">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0" style={{ backgroundColor: meta.color + '20' }}>
                  <meta.icon className="w-7 h-7" style={{ color: meta.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-2xl font-bold text-slate-900 mb-1">{meta.label}</h3>
                  <p className="text-xs text-slate-400 uppercase font-semibold tracking-wider mb-3">U.S. National Prevalence</p>
                  <div className="flex items-baseline gap-2 mb-4">
                    <span className="text-5xl font-bold" style={{ color: meta.color }}>
                      {currentDiseaseData.us_prevalence ?? '—'}
                    </span>
                    <span className="text-2xl font-semibold text-slate-400">%</span>
                    <span className="text-xs text-slate-500 ml-2">of U.S. adults</span>
                  </div>
                  <p className="text-sm text-slate-600 leading-relaxed">{currentDiseaseData.description || ''}</p>

                  {currentDiseaseData.affected && (
                    <div className="mt-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-50 border border-slate-200">
                      <Users className="w-4 h-4 text-slate-500" />
                      <span className="text-xs font-semibold text-slate-700">{currentDiseaseData.affected}</span>
                    </div>
                  )}

                  {/* Source link */}
                  {(currentDiseaseData.resources && currentDiseaseData.resources[0]?.url) && (
                    <div className="mt-5 pt-5 border-t border-slate-100">
                      <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Where this number comes from</p>
                      <a
                        href={currentDiseaseData.resources[0].url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm font-semibold text-purple-600 hover:text-purple-700 hover:underline"
                        data-testid="autoimmune-primary-source-link"
                      >
                        {currentDiseaseData.resources[0].name} <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Why no state map explainer */}
            <div className="bg-amber-50 border-2 border-amber-300 rounded-2xl p-5" data-testid="why-no-state-map">
              <div className="flex items-center gap-2 mb-3">
                <Info className="w-5 h-5 text-amber-700" />
                <p className="text-sm font-bold text-amber-900">Why no state-by-state map?</p>
              </div>
              <p className="text-xs text-amber-900 leading-relaxed mb-3">
                No U.S. federal surveillance program — <strong>CDC PLACES</strong>, <strong>BRFSS</strong>, <strong>NHIS</strong>, or
                NIH-funded registries — publishes <em>state-level</em> autoimmune disease prevalence.
              </p>
              <p className="text-xs text-amber-900 leading-relaxed">
                We show only the verified <strong>national</strong> prevalence per disease, sourced from the disease's primary authority.
                Real geographic variation likely exists (driven by UV exposure, ancestry, environmental triggers, and diagnostic access),
                but it is not reliably measured. For chronic-disease state maps with actual surveillance data, see the
                {' '}<a href="/chronic-disease" className="underline font-semibold">Chronic Disease Dashboard</a>.
              </p>
            </div>
          </div>
        </motion.section>

        {/* ========== PREVALENCE COMPARISON ========== */}
        <motion.section {...anim(0.15)} data-testid="prevalence-comparison">
          <div className="bg-white rounded-2xl border border-slate-200/60 p-6 shadow-sm">
            <div className="flex flex-wrap items-center justify-between mb-5 gap-3">
              <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-purple-600" /> Prevalence Comparison
              </h2>
              <ShareButtons title="Autoimmune Disease Prevalence Comparison" />
            </div>
            <ResponsiveContainer width="100%" height={320}>
              <BarChart data={prevalenceData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis type="number" tick={{ fontSize: 11 }} stroke="#94a3b8" unit="%" />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} stroke="#94a3b8" width={90} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="prevalence" name="US Prevalence" radius={[0, 6, 6, 0]}>
                  {prevalenceData.map((entry, idx) => (
                    <Cell key={idx} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <InsightBox
              seeing="US prevalence of 8 major autoimmune diseases, ranked by how many Americans they affect."
              matters="Hashimoto's thyroiditis is the most common autoimmune disease (5%), yet often goes undiagnosed for years. Psoriasis and RA also affect millions."
              doAbout="Autoimmune diseases often co-occur. If you have one, you're at higher risk for others. Regular monitoring and early detection are key."
              source="NIH/NIAID, CDC, AARDA 2024"
            />
          </div>
        </motion.section>

        {/* ========== DISEASE DETAIL CARDS ========== */}
        <section data-testid="disease-details">
          <h2 className="text-xl font-bold text-slate-900 mb-5 flex items-center gap-2">
            <Shield className="w-5 h-5 text-purple-600" /> Disease Profiles
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Object.entries(diseases).map(([key, d]) => {
              const m = DISEASE_META[key] || { icon: Shield, color: '#6366f1' };
              const isExpanded = expandedDisease === key;
              return (
                <motion.div
                  key={key}
                  layout
                  className={`bg-white rounded-2xl border border-slate-200/60 p-5 shadow-sm hover:shadow-md transition-shadow cursor-pointer ${isExpanded ? 'sm:col-span-2 lg:col-span-2' : ''}`}
                  onClick={() => setExpandedDisease(isExpanded ? null : key)}
                  data-testid={`disease-card-${key}`}
                >
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: m.color + '20' }}>
                      <m.icon className="w-4 h-4" style={{ color: m.color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-bold text-slate-900 truncate">{d.name}</h3>
                      <p className="text-[10px] text-slate-400">{d.affected}</p>
                    </div>
                    <ChevronDown className={`w-4 h-4 text-slate-300 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  </div>

                  <p className="text-xs text-slate-500 mb-3 line-clamp-2">{d.description}</p>

                  <div className="space-y-1.5 text-xs">
                    <div className="flex justify-between"><span className="text-slate-400">Gender Ratio</span><span className="font-semibold text-slate-700">{d.gender_ratio}</span></div>
                    <div className="flex justify-between"><span className="text-slate-400">Onset Age</span><span className="font-semibold text-slate-700">{d.avg_onset_age}</span></div>
                    <div className="flex justify-between"><span className="text-slate-400">Diagnosis Delay</span><span className="font-semibold text-red-600">{d.diagnosis_delay}</span></div>
                  </div>

                  {isExpanded && (
                    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-4 pt-4 border-t border-slate-100">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-xs font-semibold text-slate-900 mb-2">Key Symptoms</p>
                          <ul className="space-y-1">
                            {d.symptoms?.map((s, i) => (
                              <li key={i} className="text-xs text-slate-600 flex items-center gap-1.5">
                                <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: m.color }} /> {s}
                              </li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-slate-900 mb-2">Known Triggers</p>
                          <ul className="space-y-1">
                            {d.triggers?.map((t, i) => (
                              <li key={i} className="text-xs text-slate-600 flex items-center gap-1.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 shrink-0" /> {t}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      {d.resources?.length > 0 && (
                        <div className="mt-4 pt-3 border-t border-slate-100">
                          <p className="text-xs font-semibold text-slate-900 mb-2">Support & Resources</p>
                          <div className="flex flex-wrap gap-2">
                            {d.resources.map((r, i) => (
                              <a
                                key={i}
                                href={r.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-purple-50 text-purple-700 hover:bg-purple-100 transition-colors font-medium"
                                data-testid={`resource-link-${key}-${i}`}
                              >
                                {r.name} <ExternalLink className="w-3 h-3" />
                              </a>
                            ))}
                          </div>
                        </div>
                      )}
                    </motion.div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </section>

        {/* ========== WHY AUTOIMMUNE MATTERS ========== */}
        <section className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl border border-purple-200/50 p-6 sm:p-8" data-testid="why-autoimmune-matters">          <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-purple-600" /> Why Autoimmune Diseases Matter
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { title: 'More Common Than Many Realize', desc: 'Autoimmune diseases affect millions of people, yet they are still frequently under-recognized, underdiagnosed, and misunderstood in everyday health conversations.', stat: '24M affected' },
              { title: 'Diagnostic Odyssey', desc: 'Patients see an average of 4+ doctors over 4.5 years before getting diagnosed. Many are told their symptoms are "stress" or "in their head."', stat: '4.5yr avg delay' },
              { title: 'Gender Disparity', desc: '78% of autoimmune patients are female. Hormonal, genetic, and immune differences contribute. Women\'s symptoms are more often dismissed.', stat: '78% female' },
              { title: 'Co-occurrence Risk', desc: 'Having one autoimmune disease increases your risk of developing others. Up to 25% of patients develop additional autoimmune conditions.', stat: '25% co-occur' },
              { title: 'Rising Prevalence', desc: 'Autoimmune diseases are increasing 3-9% per year globally. Environmental factors, processed food, antibiotics overuse, and reduced microbial exposure may contribute.', stat: '3-9% annual rise' },
              { title: 'Economic Burden', desc: 'Autoimmune diseases cost an estimated $100+ billion annually in healthcare costs, lost productivity, and disability.', stat: '$100B+ annually' },
            ].map((item, i) => (
              <div key={i} className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-purple-100" data-testid={`why-card-${i}`}>
                <p className="text-xs font-bold text-purple-600 mb-1">{item.stat}</p>
                <h3 className="text-sm font-bold text-slate-900 mb-1.5">{item.title}</h3>
                <p className="text-xs text-slate-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ========== RELATED COMPLEX CONDITIONS (Lyme, ME/CFS, fibromyalgia, mold/CIRS) ========== */}
        <div className="rounded-2xl bg-amber-50 border border-amber-200 p-4 sm:p-5" data-testid="related-conditions-disclaimer">
          <p className="text-xs text-amber-900 leading-relaxed">
            <strong>Note on terminology:</strong> The four conditions below are <em>not</em> classical autoimmune diseases. They are
            grouped here because they frequently co-occur with autoimmune disease, share overlapping symptoms (chronic fatigue,
            widespread pain, brain fog), are often misdiagnosed as autoimmune, or involve immune dysregulation. Each one has its
            own primary surveillance source linked on its card.
          </p>
        </div>
        <ComplexConditionsSection
          title="Related &amp; Often-Misdiagnosed Conditions"
          subtitle="Lyme disease, ME/CFS, fibromyalgia, and mold-related illness (CIRS) frequently overlap with autoimmune symptoms — but each has a distinct cause and surveillance source."
          eyebrow="Adjacent to Autoimmune"
          accent="purple"
        />

        {/* Data Sources & Download */}
        <div className="bg-white rounded-2xl border border-slate-200/60 p-6 shadow-sm" data-testid="autoimmune-data-sources">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <Info className="w-5 h-5 text-purple-600" /> Data Sources & Download
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <p className="text-xs font-semibold text-slate-700 mb-2">Where this data comes from:</p>
              <ul className="space-y-2">
                {[
                  { name: "NIH / NIAID Autoimmune Research", url: "https://www.niaid.nih.gov/diseases-conditions/autoimmune-diseases" },
                  { name: "American Autoimmune Related Diseases Association", url: "https://autoimmune.org" },
                  { name: "CDC National Health Statistics", url: "https://www.cdc.gov/nchs" },
                  { name: "Peer-reviewed epidemiological studies (PubMed)", url: "https://pubmed.ncbi.nlm.nih.gov" },
                ].map((s, i) => (
                  <li key={i} className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-purple-500 shrink-0" />
                    <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-xs text-slate-600 hover:text-purple-700 transition-colors">
                      {s.name} <ExternalLink className="w-3 h-3 inline" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-700 mb-2">Download Data:</p>
              <p className="text-xs text-slate-500 mb-3">Download state-level autoimmune disease data as CSV for your own analysis.</p>
              <CsvDownloadButton dataset="autoimmune" disease={selectedDisease} label="Download Autoimmune Data (CSV)" />
            </div>
          </div>
        </div>

        {/* Source footer */}
        <p className="text-xs text-slate-400 text-center py-4">
          Sources: NIH/NIAID, CDC, American Autoimmune Related Diseases Association (AARDA), published epidemiological research 2024
        </p>
      </div>
    </div>
  );
}
